import type {MarketPlaceBid} from '@/types/MarketPlace';

import {formatOptionalDateTime} from '../utils/date';
import {formatAmountRaw} from '../utils/format';
import {getAskStatusDisplay, getBidStatusDisplay} from '../utils/status';

type BidSummaryProps = {
  bid: MarketPlaceBid;
};

const BidSummary = ({bid}: BidSummaryProps) => {
  const bidStatus = getBidStatusDisplay(
    bid.status,
    bid.expiresAt,
    bid.meta?.flags?.hasExpired,
  );
  const askStatus = bid.ask
    ? getAskStatusDisplay(bid.ask.status, bid.ask.expiresAt)
    : undefined;
  const flags = bid.meta?.flags;

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h2 className="font-semibold text-2xl mr-3">Bid #{bid.id}</h2>
        <span className={`text-sm font-medium ${bidStatus.className}`}>
          {bidStatus.label}
        </span>
        {flags?.hasExpired && (
          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-medium">
            Expired
          </span>
        )}
        {flags?.isDeactivated && (
          <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
            Deactivated
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500">Business</p>
          <p className="font-medium dark:text-gray-200">
            {bid.businessName || '-'}
          </p>
          <p className="text-xs text-gray-500">{bid.userId}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Bid Amount</p>
          <p className="font-medium dark:text-gray-200">
            {formatAmountRaw(bid.bid)}
          </p>
        </div>

        {bid.meta?.counterOfferPrice && (
          <div>
            <p className="text-sm text-gray-500">Counter Offer Price</p>
            <p className="font-medium dark:text-gray-200">
              {bid.meta.counterOfferPrice}{' '}
              {bid.ask?.quote.asset ?? bid.bid.asset}
            </p>
          </div>
        )}

        <div>
          <p className="text-sm text-gray-500">Bid Time</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDateTime(bid.createdAt)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Bid Expiration</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDateTime(bid.expiresAt)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Target Offer</p>
          <p className="font-medium dark:text-gray-200">#{bid.askId}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Offer Status</p>
          {askStatus ? (
            <span className={`text-sm font-medium ${askStatus.className}`}>
              {askStatus.label}
            </span>
          ) : (
            <p className="text-sm text-gray-500">-</p>
          )}
        </div>

        <div>
          <p className="text-sm text-gray-500">Offer Volume</p>
          {bid.ask ? (
            <p className="font-medium dark:text-gray-200">
              {formatAmountRaw(bid.ask.volume)}
            </p>
          ) : (
            <p className="text-sm text-gray-500">-</p>
          )}
        </div>

        <div>
          <p className="text-sm text-gray-500">Offer Volume Remaining</p>
          {bid.ask ? (
            <p className="font-medium dark:text-gray-200">
              {formatAmountRaw(bid.ask.availableVolume)}
            </p>
          ) : (
            <p className="text-sm text-gray-500">-</p>
          )}
        </div>

        <div>
          <p className="text-sm text-gray-500">Offer Price</p>
          {bid.ask ? (
            <p className="font-medium dark:text-gray-200">
              {/* The agreed price: the accepted offer price (counter price once
                  approved) when set, else the ask's quote. */}
              {formatAmountRaw(
                bid.meta?.acceptedOfferPrice
                  ? {
                      asset: bid.ask.quote.asset,
                      value: bid.meta.acceptedOfferPrice,
                    }
                  : bid.ask.quote,
              )}
            </p>
          ) : (
            <p className="text-sm text-gray-500">-</p>
          )}
        </div>

        <div>
          <p className="text-sm text-gray-500">Offer Expiration</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDateTime(bid.ask?.expiresAt)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BidSummary;
